import type { CrmPatientSummary } from "src-next/modules/crm-clinical/communications/types";
import { normalizeCpf } from "src-next/shared/utils/cpf";

export interface OrganizationRecipientSources {
  /** Pacientes atendidos (PatientOrganizationContext). Maior prioridade. */
  appointmentPatients?: CrmPatientSummary[];
  /** Pacientes com consentimento (Patient do MPI). */
  consentPatients?: CrmPatientSummary[];
  /** Clientes cadastrados (Person). Menor prioridade. */
  registeredClients?: CrmPatientSummary[];
}

/** CPF só com dígitos; vazio quando não for um CPF completo. */
function cpfKey(summary: CrmPatientSummary): string {
  const digits = normalizeCpf(String(summary.taxIdentifierValue ?? ""));
  return digits.length === 11 ? digits : "";
}

/**
 * Junta os três públicos sem repetir a mesma pessoa (lista "Todos").
 *
 * - Ordem de prioridade: atendido > consentimento > Person.
 *   Quem entra primeiro vence; os seguintes repetidos são descartados.
 * - Repetido = mesmo `patientId` ou mesmo CPF.
 * - O `patientId` é comparado sem o tipo do recurso. Isso também evita
 *   colisão na seleção do dialog de envio, que compara itens por `id`.
 * - Um Person com o CPF de um Patient fica fora daqui, mas continua
 *   visível no público "Clientes cadastrados".
 */
export function mergeOrganizationRecipients({
  appointmentPatients = [],
  consentPatients = [],
  registeredClients = [],
}: OrganizationRecipientSources): CrmPatientSummary[] {
  const seenIds = new Set<string>();
  const seenCpfs = new Set<string>();
  const result: CrmPatientSummary[] = [];

  const ordered = [...appointmentPatients, ...consentPatients, ...registeredClients];
  for (const summary of ordered) {
    const id = String(summary.patientId ?? "").trim();
    const cpf = cpfKey(summary);
    if (id && seenIds.has(id)) continue;
    if (cpf && seenCpfs.has(cpf)) continue;
    if (id) seenIds.add(id);
    if (cpf) seenCpfs.add(cpf);
    result.push(summary);
  }

  return result;
}
